/**
 * DYNAMIC HEADER - Clean Professional Design
 * Simple navigation with essential items only
 * SEO landing pages are hidden (traffic from search engines)
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { useSite } from '../contexts/SiteContext';

// Optimized logo
const OPTIMIZED_LOGO = "/hero/doctor-avatar-160.avif";

export const DynamicHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const timeoutRef = useRef(null);
  const location = useLocation();
  
  const { 
    theme, 
    doctor, 
    isMainSite,
    getRelatedSites
  } = useSite();

  const relatedSites = getRelatedSites ? getRelatedSites() : [];

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  // Track scroll
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Smooth dropdown handlers with delay
  const handleMouseEnter = (dropdown) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpenDropdown(dropdown);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 150); // 150ms delay for smooth transition
  };

  // Dynamic styles
  const brandStyle = { color: theme?.primaryHex || '#0e7490' };
  const buttonStyle = { 
    backgroundColor: theme?.primaryHex || '#0e7490',
    color: '#fff'
  };

  return (
    <header 
      className={`fixed top-0 z-50 w-full transition-all duration-300 ${
        isScrolled 
          ? 'bg-white shadow-md border-b border-gray-100' 
          : 'bg-white/95 backdrop-blur-sm'
      }`}
      data-testid="main-header"
    >
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="header-logo">
            <img 
              src={OPTIMIZED_LOGO}
              alt="Dr. DN Reddy - Orthopedic Surgeon"
              width="44"
              height="44"
              className="h-11 w-11 rounded-full object-cover ring-2 ring-gray-100"
              loading="eager"
              fetchpriority="high"
            />
            <div className="hidden sm:block">
              <div className="text-base font-semibold" style={brandStyle}>
                {doctor?.shortName || 'Dr. DN Reddy'}
              </div>
              <div className="text-xs text-gray-500 -mt-0.5">
                Orthopedic Surgeon
              </div>
            </div>
          </Link>

          {/* Desktop Navigation - Clean & Simple */}
          <nav className="hidden md:flex items-center gap-1">
            
            {/* Home */}
            <Link 
              to="/" 
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                location.pathname === '/' 
                  ? 'text-cyan-700' 
                  : 'text-gray-700 hover:text-cyan-700'
              }`}
              data-testid="nav-home"
            >
              Home
            </Link>

            {/* About Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => handleMouseEnter('about')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                  openDropdown === 'about' || ['/about', '/team', '/testimonials', '/fellowships'].includes(location.pathname)
                    ? 'text-cyan-700' 
                    : 'text-gray-700 hover:text-cyan-700'
                }`}
                data-testid="nav-about"
              >
                About
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openDropdown === 'about' ? 'rotate-180' : ''}`} />
              </button>
              
              <div 
                className={`absolute top-full left-0 pt-2 transition-all duration-200 ${
                  openDropdown === 'about' 
                    ? 'opacity-100 visible translate-y-0' 
                    : 'opacity-0 invisible -translate-y-2'
                }`}
              >
                <div className="w-52 bg-white rounded-lg shadow-xl border border-gray-100 py-2">
                  <Link to="/about" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-cyan-700 transition-colors">
                    About Dr. DN Reddy
                  </Link>
                  <Link to="/team" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-cyan-700 transition-colors">
                    Our Team
                  </Link>
                  <Link to="/testimonials" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-cyan-700 transition-colors">
                    Patient Reviews
                  </Link>
                  <Link to="/fellowships" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-cyan-700 transition-colors">
                    International Training
                  </Link>
                  <Link to="/publications" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-cyan-700 transition-colors" data-testid="nav-publications">
                    Publications & Research
                  </Link>
                  <Link to="/awards" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-cyan-700 transition-colors" data-testid="nav-awards">
                    Awards & Memberships
                  </Link>
                  <Link to="/conferences" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-cyan-700 transition-colors" data-testid="nav-conferences">
                    Conferences & Talks
                  </Link>
                  <Link to="/in-the-news" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-cyan-700 transition-colors" data-testid="nav-news">
                    In The News
                  </Link>
                </div>
              </div>
            </div>

            {/* Specialties - Links to specialty sites */}
            {relatedSites.length > 0 && (
              <div 
                className="relative"
                onMouseEnter={() => handleMouseEnter('specialties')}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                    openDropdown === 'specialties'
                      ? 'text-cyan-700' 
                      : 'text-gray-700 hover:text-cyan-700'
                  }`}
                  data-testid="nav-specialties"
                >
                  Specialties
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openDropdown === 'specialties' ? 'rotate-180' : ''}`} />
                </button>
                
                <div 
                  className={`absolute top-full left-0 pt-2 transition-all duration-200 ${
                    openDropdown === 'specialties' 
                      ? 'opacity-100 visible translate-y-0' 
                      : 'opacity-0 invisible -translate-y-2'
                  }`}
                >
                  <div className="w-52 bg-white rounded-lg shadow-xl border border-gray-100 py-2">
                    {relatedSites.map((site) => (
                      <a 
                        key={site.id}
                        href={`https://${site.domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-cyan-700 transition-colors"
                      >
                        <span 
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: site.theme?.primaryHex || '#0e7490' }}
                        ></span>
                        {site.bodyPart || site.title}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* For Patients Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => handleMouseEnter('patients')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                  openDropdown === 'patients' || ['/international-patients', '/faq', '/blog', '/resources'].includes(location.pathname)
                    ? 'text-cyan-700' 
                    : 'text-gray-700 hover:text-cyan-700'
                }`}
                data-testid="nav-patients"
              >
                For Patients
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openDropdown === 'patients' ? 'rotate-180' : ''}`} />
              </button>
              
              <div 
                className={`absolute top-full left-0 pt-2 transition-all duration-200 ${
                  openDropdown === 'patients' 
                    ? 'opacity-100 visible translate-y-0' 
                    : 'opacity-0 invisible -translate-y-2'
                }`}
              >
                <div className="w-52 bg-white rounded-lg shadow-xl border border-gray-100 py-2">
                  <Link to="/international-patients" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-cyan-700 transition-colors">
                    International Patients
                  </Link>
                  <Link to="/faq" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-cyan-700 transition-colors">
                    FAQ
                  </Link>
                  <Link to="/blog" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-cyan-700 transition-colors">
                    Blog
                  </Link>
                  <Link to="/resources" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-cyan-700 transition-colors">
                    Resources
                  </Link>
                </div>
              </div>
            </div>

            {/* Contact */}
            <Link 
              to="/contact" 
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                location.pathname === '/contact' 
                  ? 'text-cyan-700' 
                  : 'text-gray-700 hover:text-cyan-700'
              }`}
              data-testid="nav-contact"
            >
              Contact
            </Link>
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <a 
              href={`tel:${doctor?.phone || '+91 99599 64567'}`}
              className="text-sm font-medium text-gray-600 hover:text-cyan-700 transition-colors"
            >
              {doctor?.phone || '+91 99599 64567'}
            </a>
            <a href={doctor?.consultationLink || '#'} target="_blank" rel="noopener noreferrer">
              <Button 
                style={buttonStyle} 
                className="h-9 px-4 text-sm font-medium hover:opacity-90 transition-opacity"
                data-testid="header-cta"
              >
                <Phone className="w-4 h-4 mr-2" />
                Book Appointment
              </Button>
            </a>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div 
        className={`md:hidden bg-white border-t border-gray-100 shadow-lg overflow-hidden transition-all duration-300 ${
          isMenuOpen ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0'
        }`}
        data-testid="mobile-menu"
      >
        <div className="container mx-auto px-4 py-4 space-y-1">
          
          <Link 
            to="/" 
            className="block px-4 py-3 text-base font-medium text-gray-900 hover:bg-gray-50 rounded-lg"
            onClick={() => setIsMenuOpen(false)}
          >
            Home
          </Link>

          {/* About Section */}
          <div className="border-t border-gray-100 pt-2">
            <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase">About</div>
            <Link to="/about" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setIsMenuOpen(false)}>
              About Dr. DN Reddy
            </Link>
            <Link to="/team" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setIsMenuOpen(false)}>
              Our Team
            </Link>
            <Link to="/testimonials" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setIsMenuOpen(false)}>
              Patient Reviews
            </Link>
            <Link to="/fellowships" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setIsMenuOpen(false)}>
              International Training
            </Link>
            <Link to="/publications" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setIsMenuOpen(false)}>
              Publications & Research
            </Link>
            <Link to="/awards" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setIsMenuOpen(false)}>
              Awards & Memberships
            </Link>
            <Link to="/conferences" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setIsMenuOpen(false)}>
              Conferences & Talks
            </Link>
            <Link to="/in-the-news" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setIsMenuOpen(false)}>
              In The News
            </Link>
          </div>

          {/* Specialties Section */}
          {relatedSites.length > 0 && (
            <div className="border-t border-gray-100 pt-2">
              <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase">Specialties</div>
              {relatedSites.map((site) => (
                <a 
                  key={site.id}
                  href={`https://${site.domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <span 
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: site.theme?.primaryHex || '#0e7490' }}
                  ></span>
                  {site.bodyPart || site.title}
                </a>
              ))}
            </div>
          )}

          {/* For Patients Section */}
          <div className="border-t border-gray-100 pt-2">
            <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase">For Patients</div>
            <Link to="/international-patients" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setIsMenuOpen(false)}>
              International Patients
            </Link>
            <Link to="/faq" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setIsMenuOpen(false)}>
              FAQ
            </Link>
            <Link to="/blog" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setIsMenuOpen(false)}>
              Blog
            </Link>
            <Link to="/resources" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg" onClick={() => setIsMenuOpen(false)}>
              Resources
            </Link>
          </div>

          {/* Contact */}
          <div className="border-t border-gray-100 pt-2">
            <Link 
              to="/contact" 
              className="block px-4 py-3 text-base font-medium text-gray-900 hover:bg-gray-50 rounded-lg"
              onClick={() => setIsMenuOpen(false)}
            >
              Contact
            </Link>
          </div>

          {/* Mobile CTA */}
          <div className="border-t border-gray-100 pt-4 space-y-3">
            <a href={`tel:${doctor?.phone || '+91 99599 64567'}`} className="block">
              <Button variant="outline" className="w-full h-11">
                <Phone className="w-4 h-4 mr-2" />
                {doctor?.phone || '+91 99599 64567'}
              </Button>
            </a>
            <a href={doctor?.consultationLink || '#'} target="_blank" rel="noopener noreferrer" className="block">
              <Button style={buttonStyle} className="w-full h-11">
                Book Appointment
              </Button>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};
